import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import DateField from '../components/DateField';

type Code = {
  id: string;
  code: string;
  label: string;
  percentOff: number;
  maxUses: number;
  used: number;
  confirmed: number;
  expiresAt: string;
  active: boolean;
  createdAt: string;
};

const inMonths = (n: number) => {
  const d = new Date();
  d.setMonth(d.getMonth() + n);
  return d.toISOString().slice(0, 10);
};

export default function PromoAdminPage() {
  const [codes, setCodes] = useState<Code[] | null>(null);
  const [admin, setAdmin] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<Code[]>([]);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState('');
  const [percentOff, setPercentOff] = useState(20);
  const [maxUses, setMaxUses] = useState(50);
  const [expiresOn, setExpiresOn] = useState(inMonths(3));
  const [count, setCount] = useState(1);
  const [ownCode, setOwnCode] = useState('');

  const load = useCallback(async () => {
    try {
      const me = await api<{ admin: boolean }>('/concepts/approvals/me', { token: true });
      setAdmin(me.admin);
      if (me.admin) setCodes(await api<Code[]>('/admin/promo', { token: true }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the codes.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const fresh = await api<Code[]>('/admin/promo', {
        method: 'POST',
        token: true,
        body: { label: label.trim(), percentOff, maxUses, expiresOn, count: ownCode.trim() ? 1 : count, code: ownCode.trim() || null },
      });
      setMade(fresh);
      setOwnCode('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The codes were not made.');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(c: Code) {
    setError(null);
    try {
      await api(`/admin/promo/${c.id}/active`, { method: 'POST', token: true, body: { active: !c.active } });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not change.');
    }
  }

  if (admin === false) return <div className="card"><p>This page is for EasyLearning administrators.</p></div>;
  if (admin === null) return <p className="muted">Loading…</p>;

  const today = new Date();
  const state = (c: Code) => !c.active ? 'Switched off' : new Date(c.expiresAt) <= today ? 'Expired' : c.used >= c.maxUses ? 'Used up' : 'Live';

  return (
    <div>
      <div className="card">
        <h2>Promotion codes</h2>
        <p className="field-hint">
          Each code takes a percentage off a new customer's first payment only — renewals are full price. Uses are
          first come, first served, and each account can use one code once.
        </p>
        {error && <div className="error" role="alert">{error}</div>}
        <form onSubmit={(e) => void create(e)}>
          <div className="row">
            <div className="field">
              <label htmlFor="pc-label">Who it is for</label>
              <input id="pc-label" value={label} maxLength={120} required placeholder="e.g. Oakfield Primary, or a promoter's name" onChange={(e) => setLabel(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="pc-percent">Discount (% off first payment)</label>
              <input id="pc-percent" type="number" min={1} max={100} required value={percentOff} onChange={(e) => setPercentOff(Number(e.target.value))} />
            </div>
          </div>
          <div className="row">
            <div className="field">
              <label htmlFor="pc-uses">How many customers can use each code</label>
              <input id="pc-uses" type="number" min={1} max={100000} required value={maxUses} onChange={(e) => setMaxUses(Number(e.target.value))} />
            </div>
            <div className="field">
              <label htmlFor="pc-expires">Last day it works</label>
              <DateField id="pc-expires" required min={new Date().toISOString().slice(0, 10)} value={expiresOn} onChange={(v) => setExpiresOn(v ?? '')} />
            </div>
          </div>
          <div className="row">
            <div className="field">
              <label htmlFor="pc-count">How many codes to make</label>
              <input id="pc-count" type="number" min={1} max={50} value={ownCode.trim() ? 1 : count} disabled={!!ownCode.trim()} onChange={(e) => setCount(Number(e.target.value))} />
              <span className="field-hint">Up to 50, each with its own uses — for example one per class or event.</span>
            </div>
            <div className="field">
              <label htmlFor="pc-own">Choose the code yourself (optional)</label>
              <input id="pc-own" value={ownCode} maxLength={40} placeholder="e.g. OAKFIELD20" onChange={(e) => setOwnCode(e.target.value.toUpperCase())} />
              <span className="field-hint">Leave empty to make one from the name, like OAKFIELDPR-7KQ3PX.</span>
            </div>
          </div>
          <div className="toolbar">
            <button className="btn" type="submit" disabled={busy || !label.trim()}>{busy ? 'Making…' : count > 1 && !ownCode.trim() ? `Make ${count} codes` : 'Make code'}</button>
          </div>
        </form>
        {made.length > 0 && (
          <div className="banner" role="status" style={{ marginTop: 'var(--s4)' }}>
            <strong>{made.length === 1 ? 'New code:' : `${made.length} new codes:`}</strong>{' '}
            <code style={{ userSelect: 'all' }}>{made.map((c) => c.code).join(', ')}</code>
          </div>
        )}
      </div>

      <div className="card">
        <h2>All codes</h2>
        {!codes || codes.length === 0 ? (
          <p className="muted">No codes yet.</p>
        ) : (
          <table className="library-table">
            <thead>
              <tr>
                <th scope="col">Code</th>
                <th scope="col">For</th>
                <th scope="col">Off</th>
                <th scope="col">Used</th>
                <th scope="col">Works until</th>
                <th scope="col">State</th>
                <th scope="col"><span className="visually-hidden">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.id}>
                  <td><code style={{ userSelect: 'all' }}>{c.code}</code></td>
                  <td data-label="For">{c.label}</td>
                  <td data-label="Off">{c.percentOff}%</td>
                  <td data-label="Used">
                    {c.confirmed} paid of {c.maxUses}
                    {c.used > c.confirmed && <span className="field-hint"> · {c.used - c.confirmed} at checkout now</span>}
                  </td>
                  <td data-label="Works until">{new Date(new Date(c.expiresAt).getTime() - 1).toLocaleDateString()}</td>
                  <td data-label="State">{state(c)}</td>
                  <td>
                    <button type="button" className="btn ghost" onClick={() => void toggle(c)}>{c.active ? 'Switch off' : 'Switch on'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
